import React from 'react';
import styled from 'styled-components';
import { defineMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import IconQuestionMark from './icons/IconQuestionMark';
import { useCondensedTheme } from './CondensedTheme';


function QuestionMarkButton({
    isFocused = false,
    buttonSize = GRANDFATHERED_ICON_SIZE,
    onClick,
    className,
    ...otherProps
}) {
    const intl = useIntl();
    const isCondensed = useCondensedTheme();

    return (
        <QuestionMarkButton.Button
            type="button"
            aria-label={intl.formatMessage(defineMessage({
                id: 'base-ui.QuestionMarkButton.ariaLabel',
                defaultMessage: 'Help',
            }))}
            onClick={onClick}
            isFocused={isFocused}
            buttonSize={buttonSize}
            className={classNames('rh-m-0', 'rh-p-0', 'rh-0', 'rh-border-width-0', 'rh-bg-transparent', className, {
                'is-condensed': isCondensed,
                'is-focused': isFocused,
            })}
            data-name="question-mark-button"
            {...otherProps}
        >
            <IconQuestionMark
                className="question-mark-icon rh-width-100p rh-height-100p"
                outlineClassName="question-mark-icon-outline"
                strokeWidth={isCondensed ? '3px' : '2px'}
                outlineWidth={isCondensed ? '3px' : '2px'}
            />
        </QuestionMarkButton.Button>
    );
}

const TRANSITION_DURATION = '300ms';
const GRANDFATHERED_ICON_SIZE = '1.75rem';        // grandfathering-in the pre-existing default


QuestionMarkButton.propTypes = {
    buttonSize: PropTypes.oneOf(Object.values(Sizes.ICONS).concat(GRANDFATHERED_ICON_SIZE)),
    onClick: PropTypes.func,
    isFocused: PropTypes.bool,
    className: PropTypes.string,
};


QuestionMarkButton.Button = styled.button`
    width: ${props => props.buttonSize};
    height: ${props => props.buttonSize};

    cursor: default;

    > .question-mark-icon {
        margin-bottom: -2px; /* removes unwanted space, makes all sizes even */

        fill: ${Colours.TRANSPARENT};
        stroke: ${Colours.BLUEBERRY_DARKEST};
        transition:
            fill ${TRANSITION_DURATION} ease-out,
            stroke ${TRANSITION_DURATION} ease-out;

        > .question-mark-icon-outline {
            fill: ${Colours.TRANSPARENT};
            stroke: ${Colours.BLUEBERRY_DARKEST};

            transition:
                fill ${TRANSITION_DURATION} ease-out,
                stroke ${TRANSITION_DURATION} ease-out;
        }
    }

    &.is-condensed {
        /* Condense the SVG by adding padding so
            we can maintain the same touch area
            for the button while reducing the SVG size */
        padding: 4px;

        > .question-mark-icon {
            /* Calculate the width by icon size minus 5px padding on both sides.
                We're using 5px instead of 4px defined earlier since Safari
                cuts off 1px of the icon. */
            width: calc(${props => props.buttonSize} - 10px);
            height: calc(${props => props.buttonSize} - 10px);

            overflow: visible;
        }
    }

    &:hover,
    &:focus,
    &:active,
    &.is-focused {
        cursor: pointer;
        outline: none;

        > .question-mark-icon {
            stroke: ${Colours.COCONUT};

            > .question-mark-icon-outline {
                fill: ${Colours.BLUEBERRY_DARK};
                stroke: ${Colours.BLUEBERRY_DARK};
            }
        }
    }
`;

export default QuestionMarkButton;
